import { View, ScrollView, ActivityIndicator } from "react-native";
import { useState, useCallback } from "react";
import { useRouter } from "expo-router";
import { Text } from "./components/Text";
import NumericKeypad from "./components/PickupDriveThru/NumericKeypad";
import OrderField from "./components/PickupDriveThru/OrderField";
import Header from "./components/PickupDriveThru/Header";
import FooterButton from "./components/PickupDriveThru/FooterButton";
import DeleteButton from "./components/PickupDriveThru/DeleteButton";
import { colors } from "./constants/styles";
import { useTranslateText } from "./utils";
import { useCustomerArrival } from "./hooks/useCustomerArrival";
import {
  canRetry,
  getCheckInErrorMessage,
} from "./components/PickupDriveThru/checkInError";
import { getOrderIdentifierPrefix } from "./constants/orderIdentifier";
import { usePickupDriveThruStyles } from "./components/PickupDriveThru/styles";

const ORDER_IDENTIFIER_PREFIX = getOrderIdentifierPrefix();
const MAX_FIELD_LENGTH = 10;

type ActiveField = "orderIdentifier" | "orderPin";

export default function PickupDriveThru() {
  const router = useRouter();
  const { t } = useTranslateText();
  const { styles, isPortrait } = usePickupDriveThruStyles();

  const [activeField, setActiveField] =
    useState<ActiveField>("orderIdentifier");
  const [orderIdentifier, setOrderIdentifier] = useState("");
  const [orderPin, setOrderPin] = useState("");
  const {
    mutate: submitArrival,
    isPending,
    error,
    reset,
  } = useCustomerArrival();

  const setActiveValue =
    activeField === "orderIdentifier" ? setOrderIdentifier : setOrderPin;
  const canSubmit =
    orderIdentifier.length > 0 && orderPin.length > 0 && !isPending;

  const handleSubmit = useCallback(() => {
    submitArrival(
      {
        orderId: `${ORDER_IDENTIFIER_PREFIX}${orderIdentifier}`,
        backupPin: orderPin,
      },
      { onSuccess: () => router.replace("/pickup-drive-thru-verified") },
    );
  }, [submitArrival, orderIdentifier, orderPin, router]);

  const clearError = useCallback(() => {
    if (error) reset();
  }, [error, reset]);

  return (
    <View style={styles.container} testID="pickupDriveThruContainer">
      <Header />
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.content}
        keyboardShouldPersistTaps="handled"
      >
        <Text style={styles.heading}>{t("tpos.pickupDriveThru.heading")}</Text>
        {error && (
          <View
            style={[
              styles.errorContainer,
              !canRetry(error) && styles.errorContainerNoRetry,
            ]}
            testID="errorMessage"
          >
            <Text style={styles.errorText}>
              {getCheckInErrorMessage(error, t)}
            </Text>
          </View>
        )}
        <View
          style={[styles.entryArea, isPortrait && styles.entryAreaPortrait]}
        >
          <NumericKeypad
            onDigitPress={(digit) => {
              clearError();
              setActiveValue((value) =>
                value.length < MAX_FIELD_LENGTH ? value + digit : value,
              );
            }}
          />
          <View style={[styles.fields, isPortrait && styles.fieldsPortrait]}>
            <View style={styles.fieldGroup}>
              <OrderField
                testID="orderIdentifierField"
                label={t("tpos.pickupDriveThru.posOrderIdentifier")}
                placeholder={t(
                  "tpos.pickupDriveThru.orderIdentifierPlaceholder",
                )}
                prefix={ORDER_IDENTIFIER_PREFIX}
                value={orderIdentifier}
                isActive={activeField === "orderIdentifier"}
                onPress={() => setActiveField("orderIdentifier")}
              />
              <OrderField
                testID="orderPinField"
                label={t("tpos.pickupDriveThru.orderPin")}
                placeholder={t("tpos.pickupDriveThru.orderPinPlaceholder")}
                value={orderPin}
                isActive={activeField === "orderPin"}
                onPress={() => setActiveField("orderPin")}
              />
            </View>
            <DeleteButton
              label={t("tpos.pickupDriveThru.delete")}
              onPress={() => {
                clearError();
                setActiveValue((value) => value.slice(0, -1));
              }}
            />
          </View>
        </View>
      </ScrollView>
      <FooterButton
        testID="pickupDriveThruSubmitButton"
        text={
          isPending ? (
            <ActivityIndicator color={colors.white} testID="submitSpinner" />
          ) : (
            t("tpos.pickupDriveThru.submit")
          )
        }
        disabled={!canSubmit || (error !== null && !canRetry(error))}
        onPress={handleSubmit}
      />
    </View>
  );
}
